import { useEffect, useCallback } from 'react';
import imageCache, { preloadImages } from '../utils/imageCache';

/**
 * Hook for preloading images to improve navigation performance
 */
export const useImagePreloader = () => {
  
  /**
   * Preload product images
   */
  const preloadProductImages = useCallback(async (products) => {
    if (!products || !Array.isArray(products)) return;
    
    const imageUrls = products
      .map(product => product.publicUrl || product.image)
      .filter(Boolean)
      .slice(0, 20); // Limit to first 20 images to avoid overwhelming the cache
    
    try {
      await preloadImages(imageUrls);
    } catch (error) {
      console.warn('Error preloading product images:', error);
    }
  }, []);

  /**
   * Preload logo and essential images
   */
  const preloadEssentialImages = useCallback(async () => {
    const essentialImages = [
      '/images/logo/logo.jpg',
      // Add other essential images here
    ];
    
    try {
      await preloadImages(essentialImages, { preload: true });
    } catch (error) {
      console.warn('Error preloading essential images:', error);
    }
  }, []);

  /**
   * Preload images for a specific category
   */
  const preloadCategoryImages = useCallback(async (products, category) => {
    if (!products || !Array.isArray(products)) return;
    
    const categoryProducts = category 
      ? products.filter(product => product.category === category)
      : products;
    
    await preloadProductImages(categoryProducts);
  }, [preloadProductImages]);

  /**
   * Preload images when hovering over navigation items
   */
  const preloadOnHover = useCallback((imageUrls) => {
    if (!imageUrls || !Array.isArray(imageUrls)) return;
    
    // Preload with lower priority
    setTimeout(() => {
      preloadImages(imageUrls.slice(0, 5)); // Limit to 5 images on hover
    }, 100);
  }, []);

  /**
   * Clear image cache
   */
  const clearCache = useCallback(() => {
    imageCache.clearCache();
  }, []);

  /**
   * Get cache statistics
   */
  const getCacheStats = useCallback(() => {
    return imageCache.getStats();
  }, []);

  // Preload essential images on mount
  useEffect(() => {
    preloadEssentialImages();
  }, [preloadEssentialImages]);

  return {
    preloadProductImages,
    preloadEssentialImages,
    preloadCategoryImages,
    preloadOnHover,
    clearCache,
    getCacheStats
  };
};

export default useImagePreloader;
